import React from "react";
import { formatNumber, formatDate } from "@/lib/utils";
import { numberToVietnameseWords } from "@/lib/numberToWords";
import { getCompanyConfig } from "@/config/company";
import ISOPrintHeader from "@/components/shared/print/ISOPrintHeader";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

export interface DebtReconciliationPrintData {
  reconciliationCode: string;
  month: number;
  year: number;
  customerName: string;
  customerCode?: string;
  customerAddress?: string;
  customerTaxCode?: string;
  contactPerson?: string;
  phone?: string;
  openingBalance: number;
  totalIncurred: number;
  totalPaid: number;
  closingBalance: number;
  bankAccount?: string;
  bankName?: string;
  accountHolder?: string;
  deliveries?: Array<{
    date: string | Date;
    docNo: string;
    productName: string;
    quantity: number;
    amount: number;
    notes?: string;
  }>;
  payments?: Array<{
    date: string | Date;
    receiptNo: string;
    method: string;
    amount: number;
    notes?: string;
  }>;
}

export default function DebtReconciliationPrintTemplate({
  data,
  config,
}: {
  data: DebtReconciliationPrintData;
  config?: any;
}) {
  const c = getCompanyConfig(config);
  const closingDebt =
    data.closingBalance ||
    Math.max(
      0,
      (data.openingBalance || 0) +
        (data.totalIncurred || 0) -
        (data.totalPaid || 0)
    );

  // Dynamic VietQR payload
  const bankAcc = data.bankAccount || c.bankAccount || "19036888999018";
  const bankCode = "TCB"; // Techcombank
  const vietQrPayload = `https://img.vietqr.io/image/${bankCode}-${bankAcc}-compact2.png?amount=${closingDebt}&addInfo=TT%20APV%20${encodeURIComponent(data.customerCode || "KH")}&accountName=${encodeURIComponent(c.beneficiary || c.companyName)}`;

  return (
    <div
      className="p-[10mm] text-black leading-snug text-[12px] bg-white w-full mx-auto min-h-[297mm]"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="debt-reconciliation-print"
    >
      {/* ========== COMPANY HEADER ========== */}
      <ISOPrintHeader
        config={config}
      />

      {/* ========== TITLE (CENTERED, NO QR) ========== */}
      <PrintDocumentTitle
        title="BIÊN BẢN ĐỐI SOÁT CÔNG NỢ"
        subtitle={`DEBT RECONCILIATION STATEMENT • Kỳ tháng ${data.month}/${data.year}`}
        docNo={data.reconciliationCode}
        date={new Date()}
        dateLabel="Ngày in"
      />

      {/* ========== CUSTOMER INFO (BORDERLESS 2-COL TABS) ========== */}
      <div className="mb-4">
        <PrintInfoGrid
          labelWidth="w-[125px]"
          leftColumn={[
            {
              label: "Khách hàng",
              value: `${data.customerName} (${data.customerCode || "KH"})`,
              isBold: true,
            },
            {
              label: "Địa chỉ",
              value: data.customerAddress || "—",
            },
            {
              label: "Người liên hệ",
              value: `${data.contactPerson || "Đại diện Kế toán đối chiếu"}${data.phone ? ` (${data.phone})` : ""}`,
            },
          ]}
          rightColumn={[
            {
              label: "Mã đối soát",
              value: data.reconciliationCode,
              isBold: true,
              highlight: true,
            },
            {
              label: "Mã số thuế",
              value: data.customerTaxCode || "—",
            },
            {
              label: "Hạn thanh toán",
              value: "15 ngày sau chốt sổ",
              isBold: true,
            },
          ]}
        />
      </div>

      {/* ========== 4-COLUMN DEBT SUMMARY BOX ========== */}
      <div className="grid grid-cols-4 gap-2 mb-4 text-center">
        <div className="border border-black p-2 bg-slate-50">
          <p className="text-[10px] text-slate-700 font-semibold">
            NỢ ĐẦU KỲ (A)
          </p>
          <p className="font-bold font-mono text-slate-900 text-xs mt-1">
            {formatNumber(data.openingBalance || 0)} ₫
          </p>
        </div>
        <div className="border border-black p-2 bg-indigo-50/60">
          <p className="text-[10px] text-indigo-900 font-semibold">
            PHÁT SINH TĂNG (B)
          </p>
          <p className="font-bold font-mono text-indigo-900 text-xs mt-1">
            +{formatNumber(data.totalIncurred || 0)} ₫
          </p>
        </div>
        <div className="border border-black p-2 bg-emerald-50/60">
          <p className="text-[10px] text-emerald-900 font-semibold">
            ĐÃ THANH TOÁN (C)
          </p>
          <p className="font-bold font-mono text-emerald-900 text-xs mt-1">
            -{formatNumber(data.totalPaid || 0)} ₫
          </p>
        </div>
        <div className="border-2 border-black p-2 bg-yellow-100/70">
          <p className="text-[10px] text-red-900 font-bold">
            NỢ CUỐI KỲ (A + B - C)
          </p>
          <p className="font-extrabold font-mono text-red-700 text-sm mt-1">
            {formatNumber(closingDebt)} ₫
          </p>
        </div>
      </div>

      <div className="border border-black px-2 py-1.5 mb-4 text-[11px] bg-slate-50 italic">
        <b>Số tiền nợ cuối kỳ viết bằng chữ:</b>{" "}
        {closingDebt > 0
          ? numberToVietnameseWords(closingDebt, "đồng")
          : "Không đồng"}
      </div>

      {/* ========== DELIVERIES DETAIL TABLE ========== */}
      <div className="mb-4">
        <h3 className="font-bold uppercase text-slate-900 text-[11px] mb-1">
          1. CHI TIẾT HÀNG HÓA GIAO TRONG KỲ (PHÁT SINH TĂNG)
        </h3>
        <table className="w-full border-collapse border border-black text-left text-[11px] table-fixed">
          <thead>
            <tr className="bg-slate-100 font-bold text-center">
              <th className="border border-black p-1.5" style={{ width: "6%" }}>STT</th>
              <th className="border border-black p-1.5" style={{ width: "14%" }}>Ngày giao</th>
              <th className="border border-black p-1.5" style={{ width: "16%" }}>Số Phiếu Giao</th>
              <th className="border border-black p-1.5 text-left" style={{ width: "36%" }}>Tên Sản Phẩm</th>
              <th className="border border-black p-1.5 text-right" style={{ width: "12%" }}>Số Lượng</th>
              <th className="border border-black p-1.5 text-right" style={{ width: "16%" }}>Thành Tiền (₫)</th>
            </tr>
          </thead>
          <tbody>
            {(data.deliveries || []).length > 0 ? (
              data.deliveries!.map((d, i) => (
                <tr key={i} className="break-inside-avoid">
                  <td className="border border-black p-1.5 text-center">{i + 1}</td>
                  <td className="border border-black p-1.5 text-center font-mono whitespace-nowrap">{formatDate(d.date)}</td>
                  <td className="border border-black p-1.5 text-center font-mono font-bold text-blue-700 whitespace-nowrap">{d.docNo}</td>
                  <td className="border border-black p-1.5">{d.productName}</td>
                  <td className="border border-black p-1.5 text-right font-mono whitespace-nowrap">{formatNumber(d.quantity)}</td>
                  <td className="border border-black p-1.5 text-right font-mono font-semibold whitespace-nowrap">{formatNumber(d.amount)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="border border-black p-2 text-center text-slate-500 italic">
                  Không có phát sinh giao hàng trong kỳ
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ========== PAYMENT INFO & VIETQR BOX ========== */}
      <div className="grid grid-cols-12 gap-3 mb-6 p-3 border border-black rounded-sm bg-slate-50">
        <div className="col-span-8 space-y-1 text-[11px]">
          <h4 className="font-bold uppercase text-slate-900 mb-1">
            THÔNG TIN TÀI KHOẢN THANH TOÁN (BENEFICIARY BANK ACCOUNT):
          </h4>
          <p>Tên tài khoản/ <i>Account holder</i>: <b className="uppercase">{c.beneficiary || c.companyName}</b></p>
          <p>Số tài khoản/ <i>Account No.</i>: <b className="font-mono text-blue-800 text-sm">{bankAcc}</b></p>
          <p>Ngân hàng/ <i>Bank</i>: <b>{c.bankName}</b></p>
          <p>Nội dung CK/ <i>Transfer content</i>: <b className="font-mono text-red-700">TT APV {data.customerCode || "KH"}</b></p>
        </div>
        <div className="col-span-4 flex flex-col items-center justify-center border-l border-slate-300 pl-3">
          <img
            src={vietQrPayload}
            alt="VietQR QuickPay"
            className="w-24 h-24 object-contain border border-slate-300 bg-white p-1 shadow-sm"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <span className="text-[9px] text-slate-600 font-semibold mt-1">
            Quét mã VietQR chuyển khoản
          </span>
        </div>
      </div>

      {/* ========== 2-PARTY CONFIRMATION SIGNATURES ========== */}
      <div className="flex justify-between mt-8 text-[11px] break-inside-avoid">
        <div className="text-center w-[45%]">
          <div className="font-bold uppercase mb-1">ĐẠI DIỆN BÊN BÁN (APV PACKAGING)</div>
          <div className="text-slate-500 italic text-[10px] mb-16">(Ký tên, đóng dấu xác nhận)</div>
          <div className="font-medium text-slate-800">Ban Tài Chính - Kế Toán APV</div>
        </div>
        <div className="text-center w-[45%]">
          <div className="font-bold uppercase mb-1">ĐẠI DIỆN BÊN MUA (KHÁCH HÀNG)</div>
          <div className="text-slate-500 italic text-[10px] mb-16">(Xác nhận số dư, ký tên &amp; đóng dấu)</div>
          <div className="font-medium text-slate-800">{data.customerName}</div>
        </div>
      </div>
    </div>
  );
}
